import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import Button from "../mantine/button/Button";
import actionStyles from "../ui/dataTable/ActionColumn.module.scss";
import { ActionColumn } from "../ui/dataTable/DataTable";
import Modal from "../ui/modal/Modal";
import styles from "./TeamMembersTable.module.scss";

export interface DeleteTeamMemberPopoverProps {
  id: number;
  type: "active" | "pending";
  memberName?: string;
  canDestroy?: boolean;
  canPromote?: boolean;
  canDemote?: boolean;
  canResend?: boolean;
  isSelf?: boolean | null;
  isChangeRoleDisabled: boolean;
  setEditableMembership: () => void;
}

// Warning: two model types are mixed here: memberships and invitations
// for active type it is memberships
// for pending type it is invitations
const DeleteTeamMemberPopover: React.FC<DeleteTeamMemberPopoverProps> = ({
  id,
  type,
  memberName,
  isSelf = false,
  canDestroy = true,
  canPromote = true,
  canDemote = true,
  canResend = true,
  isChangeRoleDisabled,
  setEditableMembership,
}) => {
  const queryClient = useQueryClient();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const { mutate: deleteEntity, isPending: isDeletePending } = useMutation({
    mutationFn: async () => {
      if (type === "active") {
        await apiClient.memberships.membershipsDelete(id);
      } else {
        await apiClient.invitations.invitationsDelete(id);
      }
    },
    onSuccess: () => {
      if (isSelf) {
        window.location.reload();
      }
      if (!isSelf) {
        queryClient.invalidateQueries({
          queryKey: ["activeTeamMembers"],
        });
        queryClient.invalidateQueries({
          queryKey: ["pendingTeamMembers"],
        });
      }

      enqueueSnackbar(
        isSelf
          ? "You have left the team."
          : type === "active"
            ? "Team member removed successfully"
            : "Invitation cancelled successfully",
        { variant: "success" },
      );
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      const errorMessage =
        // @ts-expect-error BE types
        error?.error?.error?.message || "Error removing team member";
      enqueueSnackbar(errorMessage, { variant: "error", persist: true });
    },
  });

  const { mutate: resendInvitation } = useMutation({
    mutationFn: async () => {
      setIsResending(true);
      await apiClient.invitations.resendCreate(id);
    },
    onSuccess: () => {
      enqueueSnackbar("Invitation resent", { variant: "success" });
      setIsResending(false);
    },
    onError: (error) => {
      setIsResending(false);
      const errorMessage =
        // @ts-expect-error BE types
        error?.error?.error?.message || "Error resending invitation";
      enqueueSnackbar(errorMessage, { variant: "error", persist: true });
    },
  });

  return (
    <>
      <ActionColumn>
        {(close) => (
          <>
            {(canPromote || canDemote) && (
              <Button
                variant="menuItem"
                className={actionStyles.popoverItem}
                disabled={isChangeRoleDisabled}
                onClick={() => {
                  close();
                  setEditableMembership();
                }}
              >
                Change role
              </Button>
            )}

            {canResend && (
              <Button
                variant="menuItem"
                className={actionStyles.popoverItem}
                disabled={isResending}
                onClick={() => resendInvitation()}
              >
                Resend Invitation
              </Button>
            )}
            <Button
              variant="menuItem"
              className={actionStyles.popoverItemRed}
              disabled={!canDestroy}
              onClick={() => {
                close();
                setIsDeleteModalOpen(true);
              }}
            >
              {isSelf
                ? "Leave Team"
                : type === "active"
                  ? "Remove Member"
                  : "Cancel Invitation"}
            </Button>
          </>
        )}
      </ActionColumn>

      <Modal
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        modalLabel={
          <h3 className={styles.modalTitle}>
            <div className={styles.dangerIconWrapper}>
              <Icon iconName="DangerIcon" width="1rem" height="1rem" />
            </div>
            {isSelf
              ? "Leave Team"
              : type === "active"
                ? "Remove Team Member"
                : "Cancel Invitation"}
          </h3>
        }
        buttonLabel={
          isSelf ? "Leave Team" : type === "active" ? "Remove" : "Cancel"
        }
        action={() => deleteEntity()}
        actionButtonColor="danger"
        isLoading={isDeletePending}
      >
        <p>
          {isSelf
            ? `You are about to leave the team. You will lose access to all team resources.`
            : type === "active"
              ? `You are going to remove ${memberName || "this team member"} from the team. They will lose access to all team resources.`
              : `You are going to cancel the invitation for ${memberName || "this email"}. The invitation will be permanently deleted.`}
        </p>
      </Modal>
    </>
  );
};

export default DeleteTeamMemberPopover;
